<template>
  <div class="ml-2">
    <validation-observer ref="observer">
      <!-- Opciones principales -->
      <div
        v-for="(item, index) in model"
        :key="index"
        class="mt-4 row align-items-center"
      >
        <div class="col-10 col-lg-6">
          <div
            class="d-flex align-items-center"
            :class="{ 'border-success': item.isCorrect }"
          >
            <input class="custom-radio mr-3" disabled type="radio" />
            <div class="w-100">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="item.name"
                  custom-error-msg="El campo es requerido."
                  error-class="position-absolute"
                  :error="errors[0]"
                />
              </validation-provider>
            </div>
          </div>
        </div>
        <div class="col-2 col-lg-6 pl-0">
          <div class="d-flex align-items-center">
            <div class="mw-15 mr-lg-3 mr-md-2">
              <i
                v-if="canQuit"
                class="pointer far fa-times-circle text-danger"
                @click="quit(index)"
              />
            </div>
            <div
              class="px-2"
              :class="{
                'border-success border rounded-pill': item.isCorrect,
              }"
              @click="markAsCorrect(index)"
            >
              <i
                class="fa-check-circle"
                :class="[
                  item.isCorrect ? 'fas text-success' : 'far text-muted',
                ]"
              ></i>
              <transition name="fade">
                <span v-if="item.isCorrect" class="text-success">
                  {{ isMobile ? '' : 'Respuesta Correcta' }}
                </span>
              </transition>
            </div>
          </div>
        </div>
      </div>

      <!-- Validación oculta: debe haber al menos una respuesta correcta -->
      <validation-provider
        v-slot="{ errors }"
        name="respuesta correcta"
        rules="hasCorrect"
      >
        <input type="hidden" :value="model" />
        <p v-if="errors.length" class="text-danger mt-2">
          {{ errors[0] }}
        </p>
      </validation-provider>

      <!-- Botones de agregar -->
      <div class="mt-4 d-flex align-items-center">
        <input v-if="canAdd" class="custom-radio mr-3" disabled type="radio" />
        <u v-if="canAdd" class="pointer text-success mr-4" @click="add">
          Agregar una opción
        </u>
        <input class="custom-radio mr-3" disabled type="radio" />
        <u class="pointer text-success" @click="addChild">
          Agregar Dependencia
        </u>
      </div>

      <!-- Preguntas dependientes -->
      <div
        v-for="(child, cqIndex) in childQuestions"
        :key="`cq-${cqIndex}`"
        class="ml-3 mt-4 row border-1 align-items-center custom-liner w-75 p-3"
      >
        <div class="col-12 pb-2 border-bottom row">
          <div class="col-10">
            <i class="fas fa-pen text-orange mr-2" />
            <span class="custom-sub-title">{{ title(child, cqIndex) }}</span>
          </div>
          <div class="col-2 d-flex justify-content-end">
            <i
              class="far fa-times-circle text-danger"
              @click="quitChild(cqIndex)"
            />
          </div>
        </div>
        <div class="my-2 col-12">
          <validation-provider v-slot="{ errors }" rules="required">
            <label class="data-label">Pregunta o Descripción:</label>
            <custom-input
              v-model="child.name"
              custom-error-msg="El campo es requerido."
              :error="errors[0]"
              maxlength="500"
              placeholder="Escribe tu Pregunta o Descripción"
              :disabled="isReadOnly"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-6 my-2">
          <validation-provider v-slot="{ errors }" rules="required">
            <label class="data-label">Opción de Respuesta:</label>
            <b-form-select
              v-model="child.typeId"
              :options="types"
              :class="{ invalid: errors.length > 0 }"
              class="custom-bg-green fa font-weight-normal"
              :disabled="isReadOnly"
            >
              <template #first>
                <b-form-select-option :value="null" selected hidden>
                  -- Seleccione --
                </b-form-select-option>
              </template>
            </b-form-select>
            <custom-error
              v-if="errors.length > 0"
              message="El campo es requerido"
              variant="no-border mt-1"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-6 my-2" @click="getUnselected(child)">
          <validation-provider v-slot="{ errors }" rules="required">
            <label class="data-label">Enlazar a:</label>
            <custom-drop-down
              v-model="child.linkedAnswer"
              :options="linkedOptions"
              :clearable="false"
              :error="errors[0]"
              custom-error-msg="El campo es requerido."
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="ddl-linked-answer"
              :disabled="isReadOnly"
            />
          </validation-provider>
        </div>
        <div class="col-12 my-2">
          <div v-if="isOpenText(child)">
            <open-text />
          </div>
          <div v-else-if="isSimpleChoiceHorizontal(child)">
            <simple-choice-horizontal v-model="child.answers" />
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomError from 'custom-error';
import CustomTextArea from 'CustomTextArea';

import OpenText from './OpenText';
import SimpleChoiceHorizontal from './SimpleChoiceHorizontal';

import alertMixin from 'utils/mixins/alert.mixin';
import Types from '@/utils/constants/operations-dashboard-management/questionnaireQuestionTypes';

import { extend } from 'vee-validate';

extend('hasCorrect', {
  message: 'Debe marcar al menos una respuesta como correcta.',
  validate(value) {
    return value.some((item) => item.isCorrect);
  },
});

export default {
  name: 'SimpleWithChild',
  components: {
    CustomInput,
    CustomError,
    CustomTextArea,
    CustomDropDown,
    OpenText,
    SimpleChoiceHorizontal,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    min: {
      type: Number,
      default: () => 2,
    },
    max: {
      type: Number,
      default: () => 6,
    },
    types: {
      type: Array,
      default: () => [],
    },
    childQuestions: {
      type: Array,
      default: () => [],
    },
    parentIndex: {
      type: Number,
      default: () => 0,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      focusedChild: null,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    isOpenText() {
      return ({ typeId }) => typeId === Types.Text;
    },
    isSimpleChoiceHorizontal() {
      return ({ typeId }) => typeId === Types.SimpleChoiceHorizontal;
    },
    canAdd() {
      return !this.isReadOnly && this.model.length < this.max;
    },
    canQuit() {
      return !this.isReadOnly && this.model.length > this.min;
    },
    title() {
      return (item, index) =>
        item.name ?? `Pregunta ${this.parentIndex + 1}.${index + 1}`;
    },
    linkedOptions() {
      const selectedIds = this.childQuestions
        .filter((x) => x.linkedAnswer)
        .map((y) => y.linkedAnswer.id);
      return this.model.filter((x) => x.name && !selectedIds.includes(x.id));
    },
  },
  mounted() {
    if (this.model.length === 0) [...Array(this.min)].forEach(this.add);
  },
  methods: {
    markAsCorrect(index) {
      this.model.forEach((x, i) => (x.isCorrect = i === index));
    },
    getUnselected(item) {
      this.focusedChild = item.linkedAnswer;
    },
    add() {
      if (!this.canAdd) return;
      const id = this.model.length + 1;
      this.model.push({ id, name: null, type: null, isCorrect: false });
    },
    addChild() {
      if (this.model.length <= this.childQuestions.length) return;

      const id = this.childQuestions.length + 1;
      this.childQuestions.push({
        id,
        name: null,
        type: null,
        linkedAnswer: null,
        answers: [],
      });
    },
    async quit(index) {
      if (!this.canQuit) return;
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea remover la opción de respuesta documentada?',
        'Si',
        'No'
      );
      if (!confirmed || this.model.length <= this.min) return;
      const item = this.model[index];
      this.childQuestions.forEach((x) => {
        x.linkedAnswer =
          x.linkedAnswer?.id == item?.id || x.linkedAnswer?.name == item?.name
            ? null
            : x.linkedAnswer;
      });
      this.model.splice(index, 1);
    },
    async quitChild(index) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea remover la dependencia documentada?',
        'Si',
        'No'
      );
      if (!confirmed) return;
      this.childQuestions.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.form-control,
::v-deep .form-control,
.ddl-linked-answer ::v-deep .vs__dropdown-toggle,
.custom-bg-green {
  background-color: #f1f6f4;
  font-family: 'Font Awesome 5 Pro', 'Lato', sans-serif;
}
.mw-15 {
  min-width: 15px;
}
.text-gray {
  color: #9e9e9e !important;
}
.custom-radio {
  transform: scale(2.1);
  border: 1px solid #e2e2e2;
  appearance: none;
  width: 14px;
  height: 13px;
  border-radius: 50%;
  background-color: #f0f0f0;
}
.custom-liner {
  border-left: solid #d9e5df;
}

.custom-sub-title {
  font-size: medium;
  font-weight: 600;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.pointer {
  cursor: pointer;
}
.border-success ::v-deep input,
.border-success .custom-radio {
  border-color: #2db782;
}
</style>
